import React, {
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import { BiPencil, BiTrash } from "react-icons/bi";
import Confirm from "../../../../components/confirm/Confirm";
import { Json } from "./base";
import { FunctionValue as ParameterValue } from "./Function";

export function Parameters({
    json, onChange, parameterNames, canAdd, canDelete, isNameable,
}: {
    parameterNames: string[];
    isNameable?: boolean;
    canAdd?: boolean;
    canDelete?: boolean;
    json : Json[];
    onChange: (value: Json[]) => void;
}): ReactElement {
    const [editTemplate, setEditTemplate] = useState<Json | undefined>();

    return (
        <>
            {editTemplate != null && (
                <Confirm
                    shouldShow
                    confirmText="Save"
                    onHide={() => setEditTemplate(undefined)}
                    size={editTemplate.function ? "xl" : undefined}
                    onConfirm={evt => {
                        evt?.stopPropagation();
                        const parameters = (json ?? []).filter(row => row.name !== editTemplate.name);

                        if (editTemplate) {
                            onChange([
                                ...parameters,
                                (editTemplate as Json),
                            ]);
                        }

                        setEditTemplate(undefined);
                    }}
                >
                    <div>
                        {canAdd && (
                            <Form.Group className="mb-5">
                                <Form.Label className="fw-bolder">Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Name"
                                    required
                                    onChange={evt => setEditTemplate({ ...editTemplate, name: evt.target.value })}
                                    value={editTemplate.name as string ?? ""}
                                />
                                <Form.Text>
                                    The name of the parameter
                                </Form.Text>
                            </Form.Group>
                        )}
                        {isNameable ? (
                            // eslint-disable-next-line @typescript-eslint/no-use-before-define
                            <Template
                                json={editTemplate?.value as Json}
                                onChange={value => setEditTemplate({ ...editTemplate, value })}
                            />
                        ) : (
                            <ParameterValue
                                json={editTemplate?.value as Json ?? {}}
                                onChange={value => setEditTemplate({ ...editTemplate, value })}
                            />

                        )}
                    </div>
                </Confirm>
            )}
            {canAdd && (
                <button
                    type="button"
                    className="btn btn-primary btn-sm p-1"
                    onClick={() => setEditTemplate({})}
                >
                    Add
                </button>
            )}
            <ul className="list-group">
                {parameterNames?.map(paramName => {
                    const parameter = json?.find(param => param.name === paramName);

                    return (
                        <li className="list-group-item d-flex justify-content-between">
                            <div>
                                {paramName as string}
                                {parameter && (
                                    <small className="text-light ms-2">
                                        {JSON.stringify(parameter.value)}
                                    </small>
                                )}

                            </div>
                            <div>
                                <button
                                    className="btn btn-sm btn-none"
                                    type="button"
                                    onClick={() => {
                                        setEditTemplate(parameter ?? {
                                            name: paramName,
                                        });
                                    }}
                                >
                                    <BiPencil className="text-light" />
                                </button>

                                {canDelete && (
                                    <button
                                        className="btn btn-sm btn-none"
                                        type="button"
                                        onClick={() => {
                                            onChange(json.filter(row => row.name !== paramName));
                                        }}
                                    >
                                        <BiTrash className="text-light" />
                                    </button>

                                )}
                            </div>

                        </li>
                    );
                })}
            </ul>
        </>
    );
}
Parameters.defaultProps = {
    canAdd: false,
    canDelete: false,
    isNameable: false,
};

export function NamedParameters({
    json, onChange, title,
}: {
    json : Json[];
    onChange: (value: Json[]) => void;
    title?: string ;
}): ReactElement {
    return (
        <div>
            <h5>{title || "Parameters"}</h5>
            <Parameters
                parameterNames={json?.map(row => row.name) as string[] ?? []}
                json={json}
                onChange={onChange}
                canAdd
                canDelete
                isNameable
            />
        </div>
    );
}
NamedParameters.defaultProps = {
    title: "",
};

export function MapParameters({
    json, onChange, title,
}: {
    json : Json;
    onChange: (value: Json) => void;
    title?: string ;
}): ReactElement {
    const list = json ? Object.entries(json).map(([name, value]) => ({ name, value })) : [];

    return (
        <div>
            <h5>{title || "Parameters"}</h5>
            <Parameters
                parameterNames={list?.map(row => row.name) as string[] ?? []}
                json={list}
                onChange={value => onChange({ ...value?.toIdMap("name", "value") })}
                canAdd
                canDelete
                isNameable
            />
        </div>
    );
}
MapParameters.defaultProps = {
    title: "",
};

export default function Template({
    json, onChange, isRequired,
}: {
    json : Json | undefined;
    onChange: (value: Json) => void;
    isRequired?: boolean;
}): ReactElement {
    const [template, setTemplate] = useState<Json>(json ?? {});

    const params = (template.template as string)?.extractSymbolsWithRegExp(/\{\{([^}]+)\}\}/g);

    useEffect(() => {
        onChange(template);
    }, [template]);
    return (
        <div>
            <Form.Control
                type="text"
                placeholder="Hello {{value}}"
                required={isRequired}
                onChange={evt => setTemplate({
                    ...template,
                    template: evt.target.value,
                })}
                value={template.template as string ?? ""}
            />
            <div>Template Parameters</div>
            <Parameters
                parameterNames={params?.symbols}
                json={json?.parameters as Json[]}
                onChange={values => setTemplate({
                    ...template,
                    parameters: values as Json[],
                })}
            />
        </div>
    );
}

Template.defaultProps = {
    isRequired: false,
};
